import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IoService } from '../io/io.service';
import { ClientService } from '../client/client.service';
import { ConnectivityService } from '../connectivity/connectivity.service';
import { DeviceSettingsService } from '../device-settings/device-settings.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { CachedItem, CacheLocation, CacheGroup, InventoryCacheDb, SyncResult, cacheDbName, runCatalogSync, SCHEMA_VERSION } from './inventory-cache-db';

export { CachedItem, CacheLocation, CacheGroup } from './inventory-cache-db';

export type CacheState = 'off' | 'empty' | 'loading' | 'syncing' | 'ready' | 'error';

export interface CacheStatus {
  state: CacheState;
  itemCount: number;
  lastSync: Date | null;
  progress: { pages: number; items: number } | null;
  error: string | null;
}

const SYNC_INTERVAL_MS = 10 * 60 * 1000;

/**
 * Local Inventory Cache: the whole active catalog in IndexedDB, refreshed by
 * server-timestamped deltas, so a scan on a transfer resolves on the device
 * (instantly, and with no connection). Enabled per device from Settings.
 */
@Injectable({
  providedIn: 'root'
})
export class InventoryCacheService {

  private db: InventoryCacheDb | null = null;
  private status = new BehaviorSubject<CacheStatus>({ state: 'off', itemCount: 0, lastSync: null, progress: null, error: null });
  private syncing = false;
  private syncTimer: any = null;
  private locations: CacheLocation[] = [];
  private locationIndex: { [id: string]: number } = {};
  private groups: { [id: string]: CacheGroup } = {};
  private metaLoaded = false;
  // True from the first page of a FULL load until its meta is written: the
  // catalog is partial, so scans must keep resolving on the server.
  private fullLoading = false;

  constructor(
    private zone: NgZone,
    private ioService: IoService,
    private clientService: ClientService,
    private connectivityService: ConnectivityService,
    private deviceSettingsService: DeviceSettingsService,
    private authenticationService: AuthenticationService,
  ) {
    this.deviceSettingsService.settingsChangedObservable().subscribe(() => this.applySetting());
    this.connectivityService.onlineObservable().subscribe(online => {
      if (online && this.isEnabled()) {
        this.sync();
      }
    });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.isEnabled()) {
        this.sync();
      }
    });
    this.authenticationService.authenticationStateChangedObservable().subscribe(() => {
      if (this.isEnabled() && this.authenticationService.isSignedIn()) {
        this.sync();
      }
    });
    this.applySetting();
  }

  // ---- state ---------------------------------------------------------------

  isEnabled(): boolean {
    return this.deviceSettingsService.isLocalInventoryCacheEnabled();
  }

  // Usable for scanning: enabled and holding a catalog (a sync may be running).
  isReady(): boolean {
    const state = this.status.value.state;
    return this.isEnabled() && !this.fullLoading && this.status.value.itemCount > 0 && (state === 'ready' || state === 'syncing');
  }

  statusObservable(): Observable<CacheStatus> {
    return this.status.asObservable();
  }

  getStatus(): CacheStatus {
    return this.status.value;
  }

  getLocations(): CacheLocation[] {
    return this.locations;
  }

  groupName(id: string | null): string | null {
    const group = id ? this.groups[String(id)] : null;
    return group ? group.name : null;
  }

  private dbName(): string {
    return cacheDbName(this.clientService.getRfId());
  }

  private database(): InventoryCacheDb {
    if (!this.db) {
      this.db = new InventoryCacheDb(this.dbName());
    }
    return this.db;
  }

  private patchStatus(patch: Partial<CacheStatus>) {
    this.status.next({ ...this.status.value, ...patch });
  }

  private async applySetting() {
    if (!this.isEnabled()) {
      this.stopTimer();
      this.patchStatus({ state: 'off', progress: null, error: null });
      return;
    }
    await this.loadMeta();
    this.startTimer();
    if (this.authenticationService.isSignedIn()) {
      this.sync();
    }
  }

  private startTimer() {
    if (this.syncTimer) {
      return;
    }
    this.syncTimer = setInterval(() => {
      if (!document.hidden && this.connectivityService.isOnline()) {
        this.sync();
      }
    }, SYNC_INTERVAL_MS);
  }

  private stopTimer() {
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
      this.syncTimer = null;
    }
  }

  private async loadMeta() {
    try {
      const db = this.database();
      const meta = await db.meta.get('state');
      this.locations = (meta && meta.locations) || [];
      this.locationIndex = {};
      this.locations.forEach((location, index) => { this.locationIndex[location._id] = index; });
      this.groups = {};
      for (const group of ((meta && meta.groups) || [])) {
        this.groups[group._id] = group;
      }
      const itemCount = await db.items.count();
      this.metaLoaded = true;
      if (this.syncing) {
        // A sync owns the state while it runs (a meta reload mid-load must
        // not flip a half-filled catalog to "ready").
        this.patchStatus({ itemCount, lastSync: meta && meta.lastSync ? new Date(meta.lastSync) : null });
        return;
      }
      this.patchStatus({
        state: itemCount > 0 ? 'ready' : 'empty',
        itemCount,
        lastSync: meta && meta.lastSync ? new Date(meta.lastSync) : null,
        error: null,
      });
    } catch (e) {
      console.error('inventory cache: meta load failed', e);
      this.patchStatus({ state: 'error', error: 'This browser cannot store the cache (IndexedDB unavailable).' });
    }
  }

  // ---- sync ----------------------------------------------------------------

  // Pulls the catalog: everything when the cache is empty, outdated or the
  // schema changed, otherwise only what changed since the last server time.
  // The pages are fetched, parsed and written by a web worker so the page
  // thread (and the scanner wedge feeding it) is never blocked; browsers
  // without workers run the same loop inline.
  async sync(force = false): Promise<void> {
    if (!this.isEnabled() || this.syncing || !this.connectivityService.isOnline()) {
      return;
    }
    this.syncing = true;
    const db = this.database();
    try {
      if (!this.metaLoaded) {
        await this.loadMeta();
      }
      const meta = (await db.meta.get('state')) || { key: 'state' };
      const full = force || !meta.since || meta.schemaVersion !== SCHEMA_VERSION || this.status.value.itemCount === 0;
      this.fullLoading = full;
      this.patchStatus({ state: full ? 'loading' : 'syncing', progress: { pages: 0, items: 0 }, error: null });

      const request = {
        full,
        since: meta.since || null,
        locationId: this.authenticationService.getSessionLocationId() || null,
      };
      const onProgress = (pages: number, items: number) => this.patchStatus({ progress: { pages, items } });
      const result: SyncResult = (typeof Worker !== 'undefined')
        ? await this.syncInWorker(request, onProgress)
        : await runCatalogSync(db, request, body => this.ioService.post('/inventoryCache/sync', body), onProgress);

      this.locations = result.locations || [];
      this.locationIndex = {};
      this.locations.forEach((location, index) => { this.locationIndex[location._id] = index; });
      this.groups = {};
      for (const group of (result.groups || [])) {
        this.groups[group._id] = group;
      }
      this.fullLoading = false;
      this.patchStatus({ state: 'ready', itemCount: result.itemCount, lastSync: new Date(), progress: null, error: null });
    } catch (e) {
      const message = (e && e.error && e.error.msg) || (e && e.message) || 'Sync failed.';
      console.error('inventory cache: sync failed', e);
      if (e && e.status === 0) {
        this.connectivityService.markOffline();
      }
      const itemCount = await db.items.count().catch(() => this.status.value.itemCount);
      // An interrupted full load leaves a partial catalog: stay unusable
      // (empty/error) until a later sync completes; a failed delta keeps the
      // complete catalog it already had.
      const usable = itemCount > 0 && !this.fullLoading;
      this.patchStatus({ state: usable ? 'ready' : 'error', itemCount, progress: null, error: e && e.status === 0 ? null : message });
    } finally {
      this.syncing = false;
    }
  }

  private syncInWorker(request: { full: boolean; since: string | null; locationId: string | null },
                       onProgress: (pages: number, items: number) => void): Promise<SyncResult> {
    return new Promise<SyncResult>((resolve, reject) => {
      // tslint:disable-next-line:whitespace (tslint predates import.meta)
      const worker = new Worker(new URL('./inventory-sync.worker', import.meta.url), { type: 'module' });
      const finish = (fn: () => void) => {
        worker.terminate();
        this.zone.run(fn);
      };
      worker.onmessage = ({ data }) => {
        if (!data) { return; }
        if (data.type === 'progress') {
          this.zone.run(() => onProgress(data.pages, data.items));
        } else if (data.type === 'done') {
          finish(() => resolve({
            itemCount: data.itemCount,
            serverTime: data.serverTime,
            locations: data.locations || [],
            groups: data.groups || [],
          }));
        } else if (data.type === 'error') {
          finish(() => reject({ status: data.status, error: { msg: data.message }, message: data.message }));
        }
      };
      worker.onerror = (event) => {
        finish(() => reject({ status: null, message: (event && event.message) || 'The cache sync worker failed.' }));
      };
      worker.postMessage({
        cmd: 'sync',
        dbName: this.dbName(),
        serverPath: this.clientService.getServerPath(),
        token: localStorage.getItem('token'),
        rfId: this.clientService.getRfId(),
        full: request.full,
        since: request.since,
        locationId: request.locationId,
      });
    });
  }

  async clear(): Promise<void> {
    this.stopTimer();
    try {
      const db = this.database();
      await db.items.clear();
      await db.meta.clear();
    } catch (e) {
      console.error('inventory cache: clear failed', e);
    }
    this.locations = [];
    this.locationIndex = {};
    this.groups = {};
    this.patchStatus({ state: this.isEnabled() ? 'empty' : 'off', itemCount: 0, lastSync: null, progress: null, error: null });
  }

  // ---- lookups -------------------------------------------------------------

  // The scan ladder the transfer and the register share: exact code, then a
  // leading zero stripped or added, then the Retail Pro middle-ten UPC form.
  static codeVariants(code: string): string[] {
    const variants: string[] = [];
    if (code.length > 1 && code.startsWith('0')) {
      variants.push(code.substring(1));
    }
    variants.push('0' + code);
    if (/^[1-9]\d{11}$/.test(code)) {
      const middle = code.substring(1, 11);
      let checkSum = 0;
      for (let i = 0; i < 10; i++) {
        checkSum += Number(middle[i]) * (i % 2 === 0 ? 1 : 3);
      }
      variants.push(middle + String((10 - (checkSum % 10)) % 10));
    }
    return variants;
  }

  // Candidates for a scanned/typed code, nearest rung first (exact match wins
  // over variants). Vendor-linked types also resolve a retail Item # to the
  // vendor products that carry that retail item.
  async lookupByCode(rawCode: string, options: { vendorLinked?: boolean } = {}): Promise<CachedItem[]> {
    const db = this.database();
    const code = String(rawCode || '').trim().toUpperCase();
    if (!code) {
      return [];
    }
    const rungs = [[code], InventoryCacheService.codeVariants(code).map(variant => variant.toUpperCase())];
    for (const rung of rungs) {
      const hits = await db.items.where('codes').anyOf(rung).toArray();
      if (hits.length > 0) {
        return this.dedupe(hits);
      }
    }
    if (options.vendorLinked) {
      // A retail Item # scanned on a vendor transfer: the vendor products
      // linked to that retail item.
      const retail = await db.items.where('codes').anyOf(rungs[0]).toArray();
      const retailIds = retail.filter(item => item.subtype !== 'vendor-product').map(item => item._id);
      if (retailIds.length > 0) {
        const vendorRows = await db.items.where('descendantAccountIds').anyOf(retailIds).toArray();
        return this.dedupe(vendorRows.filter(item => item.subtype === 'vendor-product'));
      }
    }
    return [];
  }

  async getById(id: string): Promise<CachedItem | undefined> {
    return this.database().items.get(String(id));
  }

  private dedupe(items: CachedItem[]): CachedItem[] {
    const seen = new Set<string>();
    return items.filter(item => item.active !== false && !seen.has(item._id) && seen.add(item._id));
  }

  // Mirrors getInventoryAccounts' eligibility for a transfer type: subtype,
  // countable flag and location membership of the transfer's source/target.
  isEligible(item: CachedItem, purchase: any, typeObject: any): boolean {
    const filters = (typeObject && typeObject.inventoryFilters) || {};
    if (Array.isArray(filters.subtype) && filters.subtype.length > 0 && filters.subtype.indexOf(item.subtype) === -1) {
      return false;
    }
    if (filters.inventoryCountable === true && !item.inventoryCountable) {
      return false;
    }
    for (const key of (filters.requiredLocationKeys || [])) {
      const locationId = purchase && purchase[key];
      if (!locationId) {
        continue;
      }
      const index = this.locationIndex[String(locationId)];
      if (index === undefined || item.loc.indexOf(index) === -1) {
        return false;
      }
    }
    return true;
  }

  // The account shape addPurchaseItem consumes (what getInventoryAccounts
  // returns), built from the cached row. No balance: a line added from here
  // shows a dash for on hand until the server fills it on replay.
  toCatalogItem(item: CachedItem, purchase: any): any {
    const customerId = purchase && purchase.customerId ? String(purchase.customerId) : null;
    const locationPrice = customerId && item.priceLocation && typeof item.priceLocation[customerId] === 'number'
      ? item.priceLocation[customerId]
      : item.price;
    const account: any = {
      _id: item._id,
      name: item.name,
      sku: item.sku,
      barcode: item.barcode,
      itemNumber: item.itemNumber,
      unitDescription: item.unitDescription,
      unitOfMeasure: item.unitOfMeasure,
      attr: item.attr,
      subtype: item.subtype,
      cost: item.cost,
      price: item.price,
      locationPrice,
      categoryId: item.categoryId,
      subcategoryId: item.subcategoryId,
      categoryName: this.groupName(item.categoryId),
      subcategoryName: this.groupName(item.subcategoryId),
      inventoryCountable: item.inventoryCountable,
      stockLocation: item.stockLocation || null,
      fromCache: true,
    };
    if (typeof item.calculatedCostDefault === 'number') {
      account.calculatedCost = { default: item.calculatedCostDefault };
    }
    if (item.orderIncrement) { account.orderIncrement = item.orderIncrement; }
    if (item.caseQty) { account.caseQty = item.caseQty; }
    if (item.descendantAccountIds) { account.descendantAccountIds = item.descendantAccountIds; }
    if (item.replenishFromAccountId) {
      account.replenishFromAccountId = item.replenishFromAccountId;
      account.replenishFromAccountQty = item.replenishFromAccountQty;
    }
    return account;
  }
}
